import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';
import noDocumentImg from '../../../assets/images/png/empty-state/no-document.png';
import uploadCloudIcon from '../../../assets/images/svg/upload-cloud.svg';
import VoucherComments from '../VoucherComments';
import FinancialBreakdown from '../FinancialBreakdown/FinancialBreakdown';
import FileViewer from '../../file-upload/file-viewer';
import { postAttachmentsToExtract, startDealExtractionPolling, subscribeToDealExtractionStatus } from '../../../services/attachments-service';

// Extraction typically takes ~2 minutes; simulate a countdown
const EXTRACTION_TOTAL_SECONDS = 120;
const DEFAULT_ACTIVE_TAB = 'pdf';
const ACTIVE_TAB_STORAGE_KEY_PREFIX = 'pdf-active-tab_';
const VALID_ACTIVE_TABS = ['pdf', 'comments'];
const SparkleGlyph = () => (
  <svg className="lease-pdf-panel__sparkle-svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 2 L10.6 6.8 L15.5 8.5 L10.6 10.2 L9 15 L7.4 10.2 L2.5 8.5 L7.4 6.8 Z" fill="currentColor" />
    <path d="M18 3 L18.9 5.6 L21.5 6.5 L18.9 7.4 L18 10 L17.1 7.4 L14.5 6.5 L17.1 5.6 Z" fill="currentColor" />
    <path d="M17.5 13.5 L18.2 15.6 L20.3 16.3 L18.2 17 L17.5 19.1 L16.8 17 L14.7 16.3 L16.8 15.6 Z" fill="currentColor" />
  </svg>
);

const isOfacFileName = filename => Boolean(filename && filename.toLowerCase().includes('ofac'));

const getLeasePdfPanelTabStorageKey = (dealId, storageContext = null) => {
  if (!dealId) return null;
  return storageContext
    ? `${ACTIVE_TAB_STORAGE_KEY_PREFIX}${dealId}_${storageContext}`
    : `${ACTIVE_TAB_STORAGE_KEY_PREFIX}${dealId}`;
};

const getNormalizedActiveTab = defaultActiveTab => (
  VALID_ACTIVE_TABS.includes(defaultActiveTab) ? defaultActiveTab : DEFAULT_ACTIVE_TAB
);

const getStoredActiveTab = (dealId, defaultActiveTab = DEFAULT_ACTIVE_TAB, storageContext = null) => {
  const normalizedDefaultActiveTab = getNormalizedActiveTab(defaultActiveTab);

  if (typeof window === 'undefined') return normalizedDefaultActiveTab;

  const storageKey = getLeasePdfPanelTabStorageKey(dealId, storageContext);
  if (!storageKey) return normalizedDefaultActiveTab;

  try {
    const storedValue = window.localStorage.getItem(storageKey);
    return VALID_ACTIVE_TABS.includes(storedValue) ? storedValue : normalizedDefaultActiveTab;
  } catch (_error) {
    return normalizedDefaultActiveTab;
  }
};

const LeasePdfViewerPanel = ({
  dealFiles, initialFile, onFileChange, onFileUploaded, uploadFile, dealId,
  extractionResults, pdfPageData, setPdfPageData, setCurrentHighlightedField,
  defaultActiveTab, onFinancialCommentsTabSelected, persistActiveTab, tabStorageContext,
  allExtractionStatusMap,
}) => {
  const noFile = !initialFile?.id;
  const [activeTab, setActiveTab] = useState(() => (
    persistActiveTab
      ? getStoredActiveTab(dealId, defaultActiveTab, tabStorageContext)
      : getNormalizedActiveTab(defaultActiveTab)
  ));
  const [selectedFileId, setSelectedFileId] = useState(initialFile?.id || null);

  // Upload / extraction state
  const [uploadState, setUploadState] = useState('idle'); // 'idle' | 'uploading' | 'extracting'
  const [retryingExtraction, setRetryingExtraction] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractProgress, setExtractProgress] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(EXTRACTION_TOTAL_SECONDS);
  const [dragHighlight, setDragHighlight] = useState(false);
  const fileInputRef = useRef(null);
  const extractTimerRef = useRef(null);
  const extractionStatusUnsubscribeRef = useRef(null);
  const uploadedFileRef = useRef(null);
  const fileDropdownRef = useRef(null);

  const [fileDropdownOpen, setFileDropdownOpen] = useState(false);

  // Derived values from extraction results
  const dataExtract = extractionResults && extractionResults[0]?.data_extract;
  const ontologyId = dataExtract?.ontology_id;
  const pagesData = dataExtract?.pages;
  const annotations = { annotations: dataExtract?.annotations || {} };

  const selectedStatusFromMap = selectedFileId ? allExtractionStatusMap[String(selectedFileId)] : null;
  const fallbackStatusFromCurrentResult = extractionResults && extractionResults.length > 0
    ? extractionResults[0]?.status
    : null;
  const effectiveExtractionStatus = selectedStatusFromMap || fallbackStatusFromCurrentResult || null;

  const extractionFailed = effectiveExtractionStatus === 'Failure';
  const extractionStoppedOrReview = effectiveExtractionStatus === 'Stopped' || effectiveExtractionStatus === 'Review';
  const extractionInProgress = retryingExtraction ||
    effectiveExtractionStatus === 'Processing' || effectiveExtractionStatus === 'Queued';

  useEffect(() => {
    if (!retryingExtraction) return;
    if (effectiveExtractionStatus === 'Processing' || effectiveExtractionStatus === 'Queued' || effectiveExtractionStatus === 'Success') {
      setRetryingExtraction(false);
    }
  }, [retryingExtraction, effectiveExtractionStatus]);

  useEffect(() => {
    setActiveTab(persistActiveTab
      ? getStoredActiveTab(dealId, defaultActiveTab, tabStorageContext)
      : getNormalizedActiveTab(defaultActiveTab));
  }, [dealId, defaultActiveTab, persistActiveTab, tabStorageContext]);

  useEffect(() => {
    if (activeTab === 'comments') {
      onFinancialCommentsTabSelected();
    }
  }, [activeTab, onFinancialCommentsTabSelected]);

  useEffect(() => {
    if (activeTab !== 'pdf' && fileDropdownOpen) {
      setFileDropdownOpen(false);
    }
  }, [activeTab, fileDropdownOpen]);

  useEffect(() => {
    if (!persistActiveTab || typeof window === 'undefined') return;

    const storageKey = getLeasePdfPanelTabStorageKey(dealId, tabStorageContext);
    if (!storageKey) return;

    try {
      window.localStorage.setItem(storageKey, activeTab);
    } catch (_error) {
      // Ignore storage access errors so the panel still works without persistence.
    }
  }, [activeTab, dealId, persistActiveTab, tabStorageContext]);

  const safeDealFiles = Array.isArray(dealFiles) ? dealFiles : [];

  // Only show files that were opted-in for extraction AND have a poll-backed extraction status.
  // Dropdown visibility is driven by deal-level attachment extract polling updates.
  const VISIBLE_EXTRACTION_STATUSES = new Set(['Success', 'Processing', 'Queued', 'Stopped', 'Failure', 'Review']);
  const allOptedInFiles = safeDealFiles.filter(f => f.extraction_opted_in === true);
  const hasExtractionData = allExtractionStatusMap && Object.keys(allExtractionStatusMap).length > 0;
  const pdfFiles = allOptedInFiles.filter((f) => {
    if (!hasExtractionData) return true; // before first poll result, show all opted-in files
    const status = allExtractionStatusMap[String(f.id)];
    return status != null && VISIBLE_EXTRACTION_STATUSES.has(status);
  });
  // Files that exist on the deal but have NOT been sent for extraction
  const hasFilesButNoneOptedIn = safeDealFiles.length > 0 && pdfFiles.length === 0;
  const currentFile = pdfFiles.find(f => f.id === selectedFileId) || null;
  const currentFileName = currentFile ? (currentFile.title || currentFile.document_name || `File ${currentFile.id}`) : 'Select document';

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (fileDropdownRef.current && !fileDropdownRef.current.contains(e.target)) {
        setFileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Prevent browser from navigating to dropped files — our element-level onDrop handlers take over
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  // Sync selected file if initialFile changes externally
  useEffect(() => {
    if (initialFile && initialFile.id && initialFile.id !== selectedFileId) {
      setSelectedFileId(initialFile.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile && initialFile.id]);

  // Option C: auto-select the first extraction-opted-in file when no file is pre-resolved
  // or when the pre-resolved file is not part of the extraction dropdown.
  useEffect(() => {
    const selectedFileExists = selectedFileId && pdfFiles.some(f => f.id === selectedFileId);
    if ((!selectedFileId || !selectedFileExists) && pdfFiles.length > 0) {
      const firstFile = pdfFiles[0];
      setSelectedFileId(firstFile.id);
      if (onFileChange) onFileChange(firstFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfFiles.length, selectedFileId]);

  // Cleanup timers/subscriptions on unmount
  useEffect(() => () => {
    if (extractTimerRef.current) clearInterval(extractTimerRef.current);
    if (extractionStatusUnsubscribeRef.current) {
      extractionStatusUnsubscribeRef.current();
      extractionStatusUnsubscribeRef.current = null;
    }
  }, []);

  const startExtractionCountdown = useCallback((fileId) => {
    setExtractProgress(0);
    setSecondsRemaining(EXTRACTION_TOTAL_SECONDS);

    if (extractTimerRef.current) clearInterval(extractTimerRef.current);
    if (extractionStatusUnsubscribeRef.current) {
      extractionStatusUnsubscribeRef.current();
      extractionStatusUnsubscribeRef.current = null;
    }

    // Tick every second to animate the countdown + progress bar.
    extractTimerRef.current = setInterval(() => {
      setSecondsRemaining(s => Math.max(0, s - 1));
      setExtractProgress(p => Math.min(95, p + (95 / EXTRACTION_TOTAL_SECONDS)));
    }, 1000);

    // Ensure shared deal polling is running after a fresh extraction trigger.
    startDealExtractionPolling(dealId).catch(() => {});

    extractionStatusUnsubscribeRef.current = subscribeToDealExtractionStatus({
      dealId,
      fileId,
      onUpdate: (result) => {
        if (!result) return;
        if (result.status === 'Success' || result.status === 'Failure' || result.status === 'Stopped') {
          if (extractTimerRef.current) {
            clearInterval(extractTimerRef.current);
            extractTimerRef.current = null;
          }
          if (extractionStatusUnsubscribeRef.current) {
            extractionStatusUnsubscribeRef.current();
            extractionStatusUnsubscribeRef.current = null;
          }
          setExtractProgress(100);
          setSecondsRemaining(0);
          // Notify parent to switch to the PDF view.
          if (onFileUploaded && uploadedFileRef.current) {
            onFileUploaded(uploadedFileRef.current);
          }
          setUploadState('idle');
        }
      },
    });
  }, [dealId, onFileUploaded]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Reset input so same file can be re-selected if needed
    e.target.value = '';

    setUploadState('uploading');
    setUploadProgress(0);

    // Simulate upload progress (same pattern as file-upload.js)
    const fileSizeMB = file.size / (1024 * 1024);
    const totalMs = Math.min(1000 + fileSizeMB * 300, 10000);
    const steps = 20;
    let step = 0;
    const progressInterval = setInterval(() => {
      step += 1;
      setUploadProgress(Math.min(95, Math.round((step / steps) * 95)));
      if (step >= steps) clearInterval(progressInterval);
    }, totalMs / steps);

    const shouldOptInForExtraction = !isOfacFileName(file.name);
    const params = {
      deal: dealId,
      title: file.name,
      document_name: file.name,
      privacy: 'team',
      is_lease_document: true,
      extraction_opted_in: shouldOptInForExtraction,
    };

    uploadFile(file, params, null, shouldOptInForExtraction, true)
      .then((uploadedFile) => {
        clearInterval(progressInterval);
        setUploadProgress(100);
        if (uploadedFile && uploadedFile.id && shouldOptInForExtraction) {
          uploadedFileRef.current = uploadedFile;
          setUploadState('extracting');
          startExtractionCountdown(uploadedFile.id);
        } else {
          setUploadState('idle');
        }
      })
      .catch(() => {
        clearInterval(progressInterval);
        setUploadState('idle');
      });
  }, [dealId, uploadFile, startExtractionCountdown]);

  const handleUploadZoneDrop = useCallback((e) => {
    e.preventDefault();
    setDragHighlight(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') return;
    // Reuse the same upload logic as the file input by synthesising the event shape
    handleFileInputChange({ target: { files: [file], value: '' } });
  }, [handleFileInputChange]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const triggerExtractionRetry = useCallback((e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!currentFile || !currentFile.id || !dealId) return;

    setRetryingExtraction(true);
    postAttachmentsToExtract({
      deal: dealId,
      file: currentFile.id,
      attachment_file_key: null,
      is_abstract: true,
    })
      .then(() => startDealExtractionPolling(dealId, currentFile.id, { initialDelayMs: 12000 }))
      .catch(() => {
        setRetryingExtraction(false);
      });
  }, [currentFile, dealId]);

  return (
    <div className="lease-pdf-panel">
      {/* ── Tab bar ── */}
      <div className="lease-pdf-panel__tabs">
        <button
          type="button"
          className={`lease-pdf-panel__tab${activeTab === 'pdf' ? ' lease-pdf-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('pdf')}
        >
          PDF PREVIEW
        </button>
        <button
          type="button"
          className={`lease-pdf-panel__tab${activeTab === 'comments' ? ' lease-pdf-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          FINANCIAL BREAKDOWN &amp; COMMENTS
        </button>
      </div>

      {activeTab === 'pdf' && (
        <div className="lease-pdf-panel__file-row">
          <div className="lease-pdf-panel__file-dropdown" ref={fileDropdownRef}>
            <button
              type="button"
              className="lease-pdf-panel__file-dropdown-trigger"
              onClick={() => setFileDropdownOpen(o => !o)}
            >
              <span>{currentFileName}</span>
              <Icon iconName="chevron-down" />
            </button>
            {fileDropdownOpen && (
              <div className="lease-pdf-panel__file-dropdown-menu">
                <button
                  type="button"
                  className="lease-pdf-panel__file-dropdown-upload"
                  onClick={() => {
                    setFileDropdownOpen(false);
                    fileInputRef.current && fileInputRef.current.click();
                  }}
                >
                  <Icon iconName="plus-circle-outline" />
                  <span>Upload Document</span>
                </button>
                <div className="lease-pdf-panel__file-dropdown-divider" />
                {pdfFiles.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    className={`lease-pdf-panel__file-dropdown-item${f.id === selectedFileId ? ' lease-pdf-panel__file-dropdown-item--active' : ''}`}
                    onClick={() => {
                      setFileDropdownOpen(false);
                      setSelectedFileId(f.id);
                      if (onFileChange) onFileChange(f);
                    }}
                  >
                    {f.title || f.document_name || `File ${f.id}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        aria-hidden="true"
      />

      {/* ── Tab content ── */}
      {activeTab === 'comments' ? (
        <div className="lease-pdf-panel__comments-panel">
          <div className="lease-pdf-panel__overlay-financial">
            <FinancialBreakdown />
          </div>
          <VoucherComments />
        </div>
      ) : (
      <div className="lease-pdf-panel__viewer">
        {uploadState === 'extracting' ? (
          <div className="lease-pdf-panel__extracting-state">
            <div className="lease-pdf-panel__extracting-spinner" aria-label="Processing" />
            <p className="lease-pdf-panel__extracting-title">Lease uploaded</p>
            <p className="lease-pdf-panel__extracting-subtitle">Getting everything ready for review.</p>
            <div className="lease-pdf-panel__extracting-progress">
              <div className="lease-pdf-panel__extracting-bar">
                <div
                  className="lease-pdf-panel__extracting-fill"
                  style={{ width: `${extractProgress}%` }}
                />
              </div>
              <div className="lease-pdf-panel__extracting-footer">
                <span className="lease-pdf-panel__extracting-time">
                  {secondsRemaining > 0 ? `${formatTime(secondsRemaining)} remaining` : 'Finishing up…'}
                </span>
                <span className="lease-pdf-panel__extracting-pct">{Math.round(extractProgress)}%</span>
              </div>
            </div>
          </div>
        ) : hasFilesButNoneOptedIn ? (
          <div className="lease-pdf-panel__empty-state">
            <div className="lease-pdf-panel__empty-placeholder">
              <img
                className="lease-pdf-panel__empty-icon"
                src={noDocumentImg}
                alt=""
              />
              <p className="lease-pdf-panel__empty-title">Document not sent for extraction</p>
              <p className="lease-pdf-panel__empty-subtitle">
                This deal has uploaded files, but none have been sent for AI extraction yet.
                Upload a lease document below to extract key details and auto-fill your voucher.
              </p>
            </div>
            <button
              type="button"
              className={`lease-pdf-panel__upload-zone${dragHighlight ? ' lease-pdf-panel__upload-zone--highlight' : ''}`}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onDrop={handleUploadZoneDrop}
              onDragOver={(e) => { e.preventDefault(); setDragHighlight(true); }}
              onDragLeave={() => setDragHighlight(false)}
            >
              <div className="lease-pdf-panel__upload-header">
                <img
                  className="lease-pdf-panel__upload-icon"
                  src={uploadCloudIcon}
                  alt=""
                />
                <span className="lease-pdf-panel__upload-label">Upload a lease for extraction</span>
              </div>
              <span className="lease-pdf-panel__upload-sub">
                Upload your lease document and we&apos;ll extract key details to auto-fill your voucher.
              </span>
              <span className="lease-pdf-panel__upload-limit">There is a <strong>5GB</strong> size limit per file.</span>
            </button>
          </div>
        ) : noFile ? (
          <div className="lease-pdf-panel__empty-state">
            <div className="lease-pdf-panel__empty-placeholder">
              <img
                className="lease-pdf-panel__empty-icon"
                src={noDocumentImg}
                alt=""
              />
              <p className="lease-pdf-panel__empty-title">No preview to show yet</p>
              <p className="lease-pdf-panel__empty-subtitle">Once a lease is uploaded, the document will appear here.</p>
            </div>
            <button
              type="button"
              className={`lease-pdf-panel__upload-zone${dragHighlight ? ' lease-pdf-panel__upload-zone--highlight' : ''}`}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onDrop={handleUploadZoneDrop}
              onDragOver={(e) => { e.preventDefault(); setDragHighlight(true); }}
              onDragLeave={() => setDragHighlight(false)}
            >
              <div className="lease-pdf-panel__upload-header">
                <img
                  className="lease-pdf-panel__upload-icon"
                  src={uploadCloudIcon}
                  alt=""
                />
                <span className="lease-pdf-panel__upload-label">Drag files here or click to upload</span>
              </div>
              <span className="lease-pdf-panel__upload-sub">
                When a lease is uploaded, we&apos;ll begin extracting key details and auto-filling your voucher for you.
              </span>
              <span className="lease-pdf-panel__upload-limit">There is a <strong>5GB</strong> size limit per file.</span>
            </button>
          </div>
        ) : ontologyId ? (
          <FileViewer
            key={ontologyId}
            pagesData={pagesData}
            fileId={ontologyId}
            pdfPageData={pdfPageData}
            setPdfPageData={setPdfPageData}
            annotations={annotations}
            setCurrentHighlightedField={setCurrentHighlightedField}
          />
        ) : extractionInProgress ? (
          <div className="lease-pdf-panel__extracting-state">
            <div className="lease-pdf-panel__extracting-spinner" aria-label="Processing" />
            <p className="lease-pdf-panel__extracting-title">Extraction in progress</p>
            <p className="lease-pdf-panel__extracting-subtitle">Getting everything ready for review.</p>
          </div>
        ) : extractionFailed ? (
          <div className="lease-pdf-panel__extracting-state">
            <p className="lease-pdf-panel__extracting-title">Abstraction unavailable.</p>
            <p className="lease-pdf-panel__extracting-subtitle lease-pdf-panel__status-action-text">
              <a href="#" className="lease-pdf-panel__status-link" onClick={triggerExtractionRetry}>Retry</a>
              {' '}or continue by entering lease details manually.
            </p>
          </div>
        ) : extractionStoppedOrReview ? (
          <div className="lease-pdf-panel__extracting-state">
            <p className="lease-pdf-panel__extracting-title">Abstraction paused.</p>
            <p className="lease-pdf-panel__extracting-subtitle lease-pdf-panel__status-action-text">
              Click{' '}
              <span
                className="lease-pdf-panel__inline-sparkle"
                role="button"
                tabIndex={0}
                onClick={triggerExtractionRetry}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') triggerExtractionRetry(e); }}
                aria-label="Import Data from Document"
              >
                <SparkleGlyph />
              </span>
              {' '}to Import Data from Document.
            </p>
          </div>
        ) : (
          <div className="lease-pdf-panel__loading">Loading document&hellip;</div>
        )}

      </div>
      )}
    </div>
  );
};

LeasePdfViewerPanel.propTypes = {
  dealFiles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    document_name: PropTypes.string,
  })),
  initialFile: PropTypes.shape({
    id: PropTypes.number,
  }),
  onFileChange: PropTypes.func,
  onFileUploaded: PropTypes.func,
  uploadFile: PropTypes.func,
  dealId: PropTypes.number,
  extractionResults: PropTypes.arrayOf(PropTypes.object),
  pdfPageData: PropTypes.object,
  setPdfPageData: PropTypes.func,
  setCurrentHighlightedField: PropTypes.func,
  defaultActiveTab: PropTypes.oneOf(VALID_ACTIVE_TABS),
  onFinancialCommentsTabSelected: PropTypes.func,
  persistActiveTab: PropTypes.bool,
  tabStorageContext: PropTypes.string,
  allExtractionStatusMap: PropTypes.objectOf(PropTypes.string),
};

LeasePdfViewerPanel.defaultProps = {
  dealFiles: [],
  initialFile: null,
  onFileChange: null,
  onFileUploaded: null,
  uploadFile: null,
  dealId: null,
  onUploadClick: null,
  extractionResults: [],
  pdfPageData: { pageNumber: 1, annotationField: 'N/A' },
  setPdfPageData: () => {},
  setCurrentHighlightedField: () => {},
  defaultActiveTab: DEFAULT_ACTIVE_TAB,
  onFinancialCommentsTabSelected: () => {},
  persistActiveTab: true,
  tabStorageContext: null,
  allExtractionStatusMap: {},
};

export default LeasePdfViewerPanel;
